/**
 * Mini Rides theme behaviour (ES module, loaded once in layout/theme.liquid).
 *
 * Motion rules (web-animation-design): ease-out-quart for enter/exit, ease for hover,
 * <=300ms UI, exits ~20% faster, transform/opacity only, every animation has a
 * prefers-reduced-motion path. CSS owns the animations; JS only toggles state.
 */
const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const routes = window.Shopify?.routes?.root || '/';
const money = (cents) => {
  const format = window.MR_SETTINGS?.moneyFormat || '${{amount}}';
  const value = (cents / 100).toFixed(2);
  const noZeros = value.replace(/\.00$/, '');
  return format
    .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100))
    .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, value.replace('.', ','))
    .replace(/\{\{\s*amount\s*\}\}/, value)
    .replace(/\{\{\s*amount_without_trailing_zeros\s*\}\}/, noZeros);
};

document.documentElement.classList.add('mr-js');

/* ------------------------------------------------------------------------
   Cart: Shopify AJAX Cart API → update header count + "Added" toast
   ------------------------------------------------------------------------ */
function updateCartCount(count) {
  document.querySelectorAll('[data-cart-count]').forEach((el) => {
    el.textContent = count > 99 ? '99+' : String(count);
    el.hidden = count < 1;
  });
  const live = document.getElementById('MrCartLive');
  if (live) live.textContent = count === 1 ? '1 item in cart' : `${count} items in cart`;
}

function showToast(message) {
  const toast = document.getElementById('MrToast');
  if (!toast) return;
  toast.querySelector('[data-toast-text]').textContent = message;
  toast.hidden = false;
  requestAnimationFrame(() => toast.classList.add('is-on'));
  clearTimeout(toast._timer);
  toast._timer = setTimeout(() => {
    toast.classList.remove('is-on');
    setTimeout(() => (toast.hidden = true), reducedMotion() ? 0 : 200);
  }, 3000);
}

async function addItems(items) {
  // Local design preview (preview/index.html) has no Shopify cart
  if (typeof window.MR_PREVIEW_CART === 'function') {
    const cart = await window.MR_PREVIEW_CART(items);
    bumpCartBadge();
    return cart;
  }

  const response = await fetch(`${routes}cart/add.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ items }),
  });
  const result = await response.json();
  if (!response.ok || result.status) {
    throw new Error(result.description || result.message || 'Could not add to cart.');
  }
  const cart = await fetch(`${routes}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
  updateCartCount(cart.item_count);
  bumpCartBadge();
  showToast(items.length > 1 ? `Added your build (${items.length} items) to cart` : 'Added to cart');
  return cart;
}

function bumpCartBadge() {
  if (reducedMotion()) return;
  document.querySelectorAll('.cart-bubble').forEach((bubble) => {
    bubble.classList.remove('mr-bump');
    void bubble.offsetWidth; // restart animation
    bubble.classList.add('mr-bump');
  });
}

function flashButton(button, text = 'Added!', ms = 1500) {
  const original = button.dataset.label || button.textContent.trim();
  button.dataset.label = original;
  button.textContent = text;
  clearTimeout(button._mrTimer);
  button._mrTimer = setTimeout(() => {
    button.textContent = original;
  }, ms);
}

window.MR = { addItems, money, flashButton };

/* ------------------------------------------------------------------------
   Focus trap + scroll lock shared by configurator, popup, chat
   ------------------------------------------------------------------------ */
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])';

function trapFocus(container, event) {
  if (event.key !== 'Tab') return;
  const nodes = [...container.querySelectorAll(FOCUSABLE)].filter((n) => n.offsetParent !== null);
  if (!nodes.length) return;
  const first = nodes[0];
  const last = nodes[nodes.length - 1];
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}

let lockedScrollY = 0;
function lockScroll() {
  lockedScrollY = window.scrollY;
  document.body.style.top = `-${lockedScrollY}px`;
  document.body.classList.add('mr-scroll-lock', 'mr-scroll-lock--fixed');
}
function unlockScroll() {
  document.body.classList.remove('mr-scroll-lock', 'mr-scroll-lock--fixed');
  document.body.style.top = '';
  window.scrollTo(0, lockedScrollY);
}

/** Open/close a [data-state] overlay. CSS animates on data-state; exit waits for the transition. */
function openLayer(root) {
  root.hidden = false;
  requestAnimationFrame(() => requestAnimationFrame(() => (root.dataset.state = 'open')));
}
function closeLayer(root, exitMs) {
  root.dataset.state = 'closing';
  const done = () => {
    root.hidden = true;
    root.dataset.state = 'closed';
  };
  if (reducedMotion()) done();
  else setTimeout(done, exitMs);
}

/* ------------------------------------------------------------------------
   Branded intro — once per session, home page only, skipped on reduced motion
   ------------------------------------------------------------------------ */
function initIntro() {
  const intro = document.querySelector('.intro-overlay');
  if (!intro) return;
  const seen = (() => {
    try {
      return sessionStorage.getItem('mr-intro-seen') === '1';
    } catch {
      return false;
    }
  })();

  const finish = () => {
    intro.remove();
    document.documentElement.classList.remove('mr-intro-active');
    document.dispatchEvent(new CustomEvent('mr:intro-done'));
  };

  if (seen || reducedMotion()) {
    finish();
    return;
  }
  try {
    sessionStorage.setItem('mr-intro-seen', '1');
  } catch {}

  intro.classList.add('is-playing');
  intro.addEventListener('animationend', (event) => {
    if (event.target === intro) finish();
  });
  // Safety net in case animationend never fires (background tab etc.)
  setTimeout(() => intro.isConnected && finish(), 2400);
}

/* ------------------------------------------------------------------------
   Hero entrance (after intro) + scroll reveals
   ------------------------------------------------------------------------ */
function initHeroEntrance() {
  const heroes = document.querySelectorAll('.mr-hero');
  if (!heroes.length) return;
  const play = () => heroes.forEach((hero) => hero.classList.add('is-entered'));
  if (document.querySelector('.intro-overlay')) document.addEventListener('mr:intro-done', play, { once: true });
  else play();
}

function initReveals() {
  const targets = document.querySelectorAll('[data-reveal]:not(.is-revealed)');
  if (!targets.length) return;
  if (reducedMotion() || !('IntersectionObserver' in window)) {
    targets.forEach((el) => el.classList.add('is-revealed'));
    return;
  }
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-revealed');
        observer.unobserve(entry.target);
      });
    },
    { threshold: 0.1 }
  );
  targets.forEach((el) => {
    const group = el.closest('[data-reveal-group]');
    if (group) {
      const index = [...group.querySelectorAll('[data-reveal]')].indexOf(el);
      el.style.setProperty('--reveal-delay', `${Math.min(index, 8) * 50}ms`);
    }
    observer.observe(el);
  });
}

/* ------------------------------------------------------------------------
   Product card quick add (default variant) — separate hit target from the card link
   ------------------------------------------------------------------------ */
function initCardAdd() {
  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-mr-quick-add]');
    if (!button) return;
    event.preventDefault();
    const id = button.dataset.variantId;
    if (!id || button.disabled) return;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    const status = button.parentElement.querySelector('.mr-card__status');
    try {
      await addItems([{ id: Number(id), quantity: 1 }], button);
      button.disabled = false;
      flashButton(button);
      if (status) status.textContent = '';
    } catch (error) {
      button.disabled = false;
      if (status) status.textContent = 'Could not add to cart. Please try again.';
    } finally {
      button.removeAttribute('aria-busy');
    }
  });
}

/* ------------------------------------------------------------------------
   Configurator
   ------------------------------------------------------------------------ */
const COLOR_KEYS = ['color', 'colour', 'frame color', 'frame colour'];
const colorHex = (name) => {
  const map = window.MR_COLORS || {};
  const key = String(name || '').trim().toLowerCase();
  return map[key] || null;
};

function readProduct(id) {
  const node = document.getElementById(`mr-product-${id}`);
  if (!node) return null;
  try {
    return JSON.parse(node.textContent);
  } catch {
    return null;
  }
}

function loadState(product) {
  try {
    const saved = JSON.parse(sessionStorage.getItem(`mr-build-${product.id}`) || 'null');
    if (saved) return saved;
  } catch {}
  return null;
}
function saveState(product, state) {
  try {
    sessionStorage.setItem(`mr-build-${product.id}`, JSON.stringify(state));
  } catch {}
}

function escapeHtml(value) {
  return String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
}

class Builder {
  /**
   * @param {HTMLElement} root  .mr-builder element (inside the modal or inline on the PDP)
   * @param {object} product    data from snippets/mr-product-data.liquid
   * @param {'modal'|'inline'} mode
   */
  constructor(root, product, mode) {
    this.root = root;
    this.product = product;
    this.mode = mode;
    this.colorIndex = product.options.findIndex((o) => COLOR_KEYS.includes(String(o).toLowerCase()));
    const firstAvailable = product.variants.find((v) => v.available) || product.variants[0];
    const saved = loadState(product);
    this.state = {
      variantId: firstAvailable?.id,
      upgrades: [],
      rim: 0,
      tread: 0,
      brake: 0,
      decal: 0,
      picks: {},
      ...(saved || {}),
    };
    if (!product.variants.some((v) => v.id === this.state.variantId)) this.state.variantId = firstAvailable?.id;
    this.render();
  }

  get variant() {
    return this.product.variants.find((v) => v.id === this.state.variantId) || this.product.variants[0];
  }

  optionValues(optionIndex) {
    return [...new Set(this.product.variants.map((v) => v.options[optionIndex]))];
  }

  colorChoices() {
    if (this.colorIndex < 0) return [];
    const seen = new Map();
    this.product.variants.forEach((v) => {
      const name = v.options[this.colorIndex];
      if (!seen.has(name)) seen.set(name, v);
    });
    return [...seen.entries()].map(([name, v]) => ({ name, variant: v }));
  }

  total() {
    const p = this.product;
    let sum = this.variant?.price || 0;
    this.state.upgrades.forEach((i) => (sum += p.upgrades[i]?.price || 0));
    sum += p.rims[this.state.rim]?.price || 0;
    sum += p.decals[this.state.decal]?.price || 0;
    return sum;
  }

  render() {
    const p = this.product;
    const v = this.variant;
    const colors = this.colorChoices();
    const selectedColor = colors.find((c) => c.variant.options[this.colorIndex] === v?.options[this.colorIndex]);
    const showConfig = p.configurable;
    const cta = this.mode === 'modal' ? 'Save design &amp; checkout' : 'Add to cart';
    const image = v?.image || p.image;

    const swatch = (name, index, group, checked, hex, extra = '') => `
      <button type="button" role="radio" class="mr-swatch" data-group="${group}" data-index="${index}"
        aria-checked="${checked}" tabindex="${checked ? 0 : -1}"
        aria-label="${escapeHtml(name)}${extra}">
        <span class="mr-swatch__fill" style="${hex ? `background:${hex}` : ''}"${hex ? '' : ' data-unknown'}>${hex ? '' : escapeHtml(String(name).slice(0, 2))}</span>
        <span class="mr-swatch-ring" aria-hidden="true"></span>
      </button>`;

    let html = `
      <div class="mr-builder__media">
        <div class="mr-builder__image">
          ${image ? `<img src="${image}" alt="${escapeHtml(p.title)}${selectedColor ? ` in ${escapeHtml(selectedColor.name)}` : ''}" class="is-current" width="800" height="800">` : `<div class="mr-media-placeholder"><img src="${window.MR_SETTINGS?.logo || ''}" alt=""></div>`}
        </div>
      </div>
      <div class="mr-builder__options">
        <div class="mr-builder__head">
          <h2 class="mr-builder__title mr-italic" id="mr-builder-title-${p.id}">${this.mode === 'modal' ? 'Build your ' : ''}${escapeHtml(p.title)}</h2>
          ${p.category ? `<p class="mr-builder__cat">${escapeHtml(p.category)}</p>` : ''}
        </div>`;

    if (colors.length > 1) {
      html += `
        <fieldset class="mr-opt">
          <legend class="mr-opt__head"><span class="mr-label">${escapeHtml(p.options[this.colorIndex] || 'Frame color')}</span><span class="mr-opt__value">${escapeHtml(selectedColor?.name || '')}</span></legend>
          <div class="mr-swatches" role="radiogroup" aria-label="Frame color">
            ${colors
              .map((c, i) =>
                swatch(c.name, i, 'color', c === selectedColor, colorHex(c.name), c.variant.available ? '' : ' (unavailable)')
              )
              .join('')}
          </div>
        </fieldset>`;
    }

    // Other variant options (e.g. Size) as pill buttons
    p.options.forEach((optionName, optionIndex) => {
      if (optionIndex === this.colorIndex) return;
      const values = this.optionValues(optionIndex);
      if (values.length < 2) return;
      const currentValue = v?.options[optionIndex];
      html += `
        <fieldset class="mr-opt">
          <legend class="mr-opt__head"><span class="mr-label">${escapeHtml(optionName)}</span><span class="mr-opt__value">${escapeHtml(currentValue || '')}</span></legend>
          <div class="mr-pills mr-pills--wrap" role="radiogroup" aria-label="${escapeHtml(optionName)}">
            ${values
              .map((value, i) => {
                const available = p.variants.some((x) => x.options[optionIndex] === value && x.available);
                return `<button type="button" role="radio" class="mr-pill mr-italic" data-group="opt-${optionIndex}" data-index="${i}"
                  aria-checked="${value === currentValue}" tabindex="${value === currentValue ? 0 : -1}" ${available ? '' : 'disabled'}>${escapeHtml(value)}</button>`;
              })
              .join('')}
          </div>
        </fieldset>`;
    });

    // Per-accessory filament choices (e.g. Main color + Ribs color), sent as line item properties
    (p.colorPicks || []).forEach((pick, pickIndex) => {
      if (!pick.colors.length) return;
      const chosen = this.state.picks?.[pickIndex] ?? 0;
      html += `
        <fieldset class="mr-opt">
          <legend class="mr-opt__head"><span class="mr-label">${escapeHtml(pick.label)}</span><span class="mr-opt__value">${escapeHtml(pick.colors[chosen] || '')}</span></legend>
          <div class="mr-swatches" role="radiogroup" aria-label="${escapeHtml(pick.label)}">
            ${pick.colors.map((c, i) => swatch(c, i, `pick-${pickIndex}`, i === chosen, colorHex(c))).join('')}
          </div>
        </fieldset>`;
    });

    if (showConfig && p.upgrades.length) {
      html += `
        <fieldset class="mr-opt">
          <legend class="mr-opt__head"><span class="mr-label">Upgrades</span></legend>
          <div class="mr-upgrades">
            ${p.upgrades
              .map((u, i) => {
                const on = this.state.upgrades.includes(i);
                return `
                <label class="mr-upgrade${u.available ? '' : ' is-unavailable'}">
                  <span class="mr-upgrade__text">
                    <span class="mr-upgrade__name mr-italic">${escapeHtml(u.name)}</span>
                    <span class="mr-upgrade__desc">${escapeHtml(u.description)} — ${u.available ? `+${money(u.price)}` : 'Unavailable'}</span>
                  </span>
                  <input type="checkbox" role="switch" class="mr-switch" data-upgrade="${i}" ${on ? 'checked' : ''} ${u.available ? '' : 'disabled'}
                    aria-label="${escapeHtml(u.name)}, add ${money(u.price)}">
                </label>`;
              })
              .join('')}
          </div>
        </fieldset>`;
    }

    if (showConfig && p.rims.length > 1) {
      const rim = p.rims[this.state.rim];
      html += `
        <fieldset class="mr-opt">
          <legend class="mr-opt__head"><span class="mr-label">Rim color</span><span class="mr-opt__value">${escapeHtml(rim?.name)}</span></legend>
          ${p.rimNote ? `<p class="mr-opt__note">${escapeHtml(p.rimNote)}</p>` : ''}
          <div class="mr-swatches" role="radiogroup" aria-label="Rim color">
            ${p.rims.map((r, i) => swatch(r.name, i, 'rim', i === this.state.rim, r.hex || colorHex(r.name), r.price ? `, add ${money(r.price)}` : '')).join('')}
          </div>
        </fieldset>`;
    }

    if (showConfig && p.treads.length > 1) {
      html += `
        <fieldset class="mr-opt">
          <legend class="mr-opt__head"><span class="mr-label">Tread pattern</span><span class="mr-opt__value">${escapeHtml(p.treads[this.state.tread])}</span></legend>
          <div class="mr-pills" role="radiogroup" aria-label="Tread pattern">
            ${p.treads
              .map(
                (t, i) => `<button type="button" role="radio" class="mr-pill mr-italic" data-group="tread" data-index="${i}"
                  aria-checked="${i === this.state.tread}" tabindex="${i === this.state.tread ? 0 : -1}">${escapeHtml(t)}</button>`
              )
              .join('')}
          </div>
        </fieldset>`;
    }

    if (showConfig && p.brakes && p.brakes.length > 1) {
      html += `
        <fieldset class="mr-opt">
          <legend class="mr-opt__head"><span class="mr-label">Brake pad color</span><span class="mr-opt__value">${escapeHtml(p.brakes[this.state.brake] || p.brakes[0])}</span></legend>
          <div class="mr-pills" role="radiogroup" aria-label="Brake pad color">
            ${p.brakes
              .map(
                (t, i) => `<button type="button" role="radio" class="mr-pill mr-italic" data-group="brake" data-index="${i}"
                  aria-checked="${i === this.state.brake}" tabindex="${i === this.state.brake ? 0 : -1}">${escapeHtml(t)}</button>`
              )
              .join('')}
          </div>
        </fieldset>`;
    }

    if (showConfig && p.decals.length > 1) {
      html += `
        <fieldset class="mr-opt">
          <legend class="mr-opt__head"><span class="mr-label">${escapeHtml(p.decalLabel || 'Decals')}</span><span class="mr-opt__value">${escapeHtml(p.decals[this.state.decal]?.name)}</span></legend>
          <div class="mr-pills mr-pills--wrap" role="radiogroup" aria-label="${escapeHtml(p.decalLabel || 'Decals')}">
            ${p.decals
              .map(
                (d, i) => `<button type="button" role="radio" class="mr-pill mr-italic" data-group="decal" data-index="${i}"
                  aria-checked="${i === this.state.decal}" tabindex="${i === this.state.decal ? 0 : -1}"
                  ${d.available === false ? 'disabled' : ''}>${escapeHtml(d.name)}${d.price ? ` <span class="mr-pill__price">+${money(d.price)}</span>` : ''}</button>`
              )
              .join('')}
          </div>
        </fieldset>`;
    }

    if (p.noteLabel) {
      const noteId = `mr-note-${p.id}-${this.mode}`;
      html += `
        <div class="mr-opt mr-field">
          <label class="mr-label mr-opt__head" for="${noteId}">${escapeHtml(p.noteLabel)}</label>
          <textarea class="mr-input mr-builder__note" id="${noteId}" rows="4" maxlength="1000" data-mr-note
            placeholder="${escapeHtml(p.notePlaceholder || '')}" ${p.noteRequired ? 'required aria-required="true"' : ''}>${escapeHtml(this.state.note || '')}</textarea>
        </div>`;
    }

    if (p.description && this.mode === 'modal') {
      html += `<p class="mr-builder__desc">${escapeHtml(p.description)}</p>`;
    }

    html += `
        <div class="mr-builder__footer">
          <div class="mr-total">
            <div class="mr-total__row"><span class="mr-italic">Stock ${escapeHtml(p.title)}</span><span data-mr-base>${money(v?.price || 0)}</span></div>
            <div class="mr-total__row mr-total__row--sum"><span class="mr-italic">Total</span><span data-mr-total aria-live="polite">${money(this.total())}</span></div>
          </div>
          <button type="button" class="mr-btn mr-btn--primary mr-btn--block mr-builder__cta mr-italic" data-mr-submit ${v?.available ? '' : 'disabled'}>${v?.available ? cta : 'Sold out'}</button>
          <p class="mr-builder__error" role="alert" hidden></p>
          ${this.mode === 'modal' ? `<a class="mr-builder__more" href="${p.url}">View full details</a>` : ''}
        </div>
      </div>`;

    this.root.innerHTML = html;
    this.bind();

    // Product page: keep the big gallery image in sync with the chosen frame color
    if (this.mode === 'inline' && image) {
      const main = this.root.closest('.mr-pdp')?.querySelector('.mr-pdp__main img');
      if (main && main.getAttribute('src') !== image) {
        main.removeAttribute('srcset');
        main.src = image;
      }
    }
  }

  /** Update only price text + selections without full re-render (keeps focus, avoids flicker). */
  refresh(changedGroup) {
    saveState(this.product, this.state);
    if (changedGroup === 'upgrade') {
      this.root.querySelector('[data-mr-total]').textContent = money(this.total());
      return;
    }
    const focusedKey = document.activeElement?.dataset?.group
      ? `${document.activeElement.dataset.group}:${document.activeElement.dataset.index}`
      : null;
    const oldImg = this.root.querySelector('.mr-builder__image img.is-current');
    const oldSrc = oldImg?.getAttribute('src');
    this.render();
    const newImg = this.root.querySelector('.mr-builder__image img.is-current');
    if (oldImg && newImg && oldSrc !== newImg.getAttribute('src') && !reducedMotion()) {
      // Crossfade: keep the old image on top and fade it out
      oldImg.classList.remove('is-current');
      oldImg.classList.add('is-leaving');
      newImg.parentElement.appendChild(oldImg);
      oldImg.addEventListener('transitionend', () => oldImg.remove(), { once: true });
      requestAnimationFrame(() => (oldImg.style.opacity = '0'));
      setTimeout(() => oldImg.remove(), 400);
    }
    if (focusedKey) {
      const [g, i] = focusedKey.split(':');
      this.root.querySelector(`[data-group="${g}"][data-index="${i}"]`)?.focus();
    }
  }

  select(group, index) {
    if (group === 'color') {
      const choice = this.colorChoices()[index];
      if (!choice) return;
      // Keep other options the same where possible
      const current = this.variant;
      const match =
        this.product.variants.find(
          (v) =>
            v.options[this.colorIndex] === choice.name &&
            v.options.every((o, i) => i === this.colorIndex || o === current.options[i])
        ) || choice.variant;
      this.state.variantId = match.id;
    } else if (group.startsWith('pick-')) {
      this.state.picks = { ...(this.state.picks || {}), [Number(group.slice(5))]: index };
    } else if (group.startsWith('opt-')) {
      // Any other variant option (e.g. Size): keep the rest of the selection where possible
      const optionIndex = Number(group.slice(4));
      const value = this.optionValues(optionIndex)[index];
      const current = this.variant;
      const match =
        this.product.variants.find(
          (v) => v.options[optionIndex] === value && v.options.every((o, i) => i === optionIndex || o === current.options[i])
        ) || this.product.variants.find((v) => v.options[optionIndex] === value);
      if (match) this.state.variantId = match.id;
    } else if (group === 'rim') this.state.rim = index;
    else if (group === 'tread') this.state.tread = index;
    else if (group === 'brake') this.state.brake = index;
    else if (group === 'decal') this.state.decal = index;
    this.refresh(group);
  }

  bind() {
    this.root.querySelectorAll('[role="radio"]').forEach((el) => {
      el.addEventListener('click', () => this.select(el.dataset.group, Number(el.dataset.index)));
    });

    // Arrow keys move within a radiogroup (no animation for keyboard moves)
    this.root.querySelectorAll('[role="radiogroup"]').forEach((group) => {
      group.addEventListener('keydown', (event) => {
        const keys = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp'];
        if (!keys.includes(event.key)) return;
        event.preventDefault();
        const radios = [...group.querySelectorAll('[role="radio"]:not([disabled])')];
        const index = radios.indexOf(document.activeElement);
        const step = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : -1;
        const next = radios[(index + step + radios.length) % radios.length];
        this.root.classList.add('is-keyboard');
        this.select(next.dataset.group, Number(next.dataset.index));
      });
    });

    this.root.querySelectorAll('[data-upgrade]').forEach((input) => {
      input.addEventListener('change', () => {
        const i = Number(input.dataset.upgrade);
        this.state.upgrades = input.checked
          ? [...new Set([...this.state.upgrades, i])].sort((a, b) => a - b)
          : this.state.upgrades.filter((x) => x !== i);
        input.closest('.mr-upgrade').classList.toggle('is-on', input.checked);
        this.refresh('upgrade');
      });
      input.closest('.mr-upgrade').classList.toggle('is-on', input.checked);
    });

    this.root.querySelector('[data-mr-note]')?.addEventListener('input', (e) => {
      this.state.note = e.target.value;
      e.target.removeAttribute('aria-invalid');
    });

    this.root.querySelector('[data-mr-submit]')?.addEventListener('click', (e) => this.submit(e.currentTarget));
  }

  buildItems() {
    const p = this.product;
    const v = this.variant;
    const buildId = `MR-${Date.now().toString(36).toUpperCase()}`;
    const upgrades = this.state.upgrades.map((i) => p.upgrades[i]).filter(Boolean);
    const rim = p.rims[this.state.rim];
    const decal = p.decals[this.state.decal];
    const colorName = this.colorIndex >= 0 ? v.options[this.colorIndex] : null;

    const properties = {};
    if (colorName) properties['Frame Color'] = colorName;
    if (p.configurable) {
      properties['Upgrades'] = upgrades.length ? upgrades.map((u) => u.name).join(', ') : 'None';
      if (p.rims.length > 1) properties['Rim Color'] = rim?.name;
      if (p.treads.length > 1) properties['Tread Pattern'] = p.treads[this.state.tread];
      if (p.brakes && p.brakes.length > 1) properties['Brake Pads'] = p.brakes[this.state.brake] || p.brakes[0];
      if (p.decals.length > 1) properties['Decals'] = decal?.name || 'None';
      properties['_Build ID'] = buildId;
    }

    (p.colorPicks || []).forEach((pick, pickIndex) => {
      if (pick.colors.length) properties[pick.label] = pick.colors[this.state.picks?.[pickIndex] ?? 0];
    });

    const note = (this.state.note || '').trim();
    if (p.noteLabel && note) properties[p.noteLabel] = note;

    const items = [{ id: v.id, quantity: 1, properties }];
    const addOn = (variantId, label) =>
      items.push({ id: variantId, quantity: 1, properties: { 'For build': `${p.title} (${buildId})`, Option: label } });
    upgrades.forEach((u) => addOn(u.variantId, u.name));
    if (rim?.variantId) addOn(rim.variantId, `Rim color: ${rim.name}`);
    if (decal?.variantId) addOn(decal.variantId, `Decals: ${decal.name}`);
    return items;
  }

  async submit(button) {
    const error = this.root.querySelector('.mr-builder__error');
    error.hidden = true;
    if (!this.variant?.available) return;
    if (this.product.noteRequired && !(this.state.note || '').trim()) {
      const field = this.root.querySelector('[data-mr-note]');
      error.textContent = `Please fill in "${this.product.noteLabel}" so we know what to make.`;
      error.hidden = false;
      field?.setAttribute('aria-invalid', 'true');
      field?.focus();
      return;
    }
    if (this.product.demo && !window.MR_PREVIEW_CART) {
      // Sample bike from the design preview — not a real Shopify product yet
      error.textContent = 'This is a sample bike. Add your real products in Shopify and they will appear here, ready to buy.';
      error.hidden = false;
      return;
    }
    button.disabled = true;
    button.textContent = 'Saving…';
    try {
      await addItems(this.buildItems(), this.root);
      if (this.mode === 'modal' && window.MR_PREVIEW_CART) {
        button.disabled = false;
        button.innerHTML = 'Save design &amp; checkout';
        flashButton(button, 'Added! (preview — checkout opens in Shopify)', 2200);
      } else if (this.mode === 'modal') {
        button.textContent = 'Added! Going to checkout…';
        window.location.href = `${routes}checkout`;
      } else {
        button.disabled = false;
        button.innerHTML = 'Add to cart';
        flashButton(button);
      }
    } catch (err) {
      button.disabled = false;
      button.innerHTML = this.mode === 'modal' ? 'Save design &amp; checkout' : 'Add to cart';
      error.textContent = 'Could not add to cart. Please try again.';
      error.hidden = false;
    }
  }
}

function initConfigurator() {
  // Inline builders (product page)
  document.querySelectorAll('[data-mr-builder-inline]:not([data-bound])').forEach((root) => {
    const product = readProduct(root.dataset.productId);
    if (!product) return;
    root.dataset.bound = 'true';
    new Builder(root, product, 'inline');
  });

  const modal = document.getElementById('mr-configurator');
  if (!modal || modal.dataset.bound) return;
  modal.dataset.bound = 'true';
  const panel = modal.querySelector('.configurator-panel');
  const body = modal.querySelector('.mr-builder');
  let opener = null;

  const close = () => {
    if (modal.hidden) return;
    closeLayer(modal, 200);
    unlockScroll();
    opener?.focus({ preventScroll: true });
  };

  const open = (productId, trigger) => {
    const product = readProduct(productId);
    if (!product) return false;
    opener = trigger;
    new Builder(body, product, 'modal');
    modal.setAttribute('aria-labelledby', `mr-builder-title-${product.id}`);
    lockScroll();
    openLayer(modal);
    setTimeout(() => modal.querySelector('.configurator-close')?.focus({ preventScroll: true }), 30);
    return true;
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-mr-configure]');
    if (!trigger || event.metaKey || event.ctrlKey || event.shiftKey) return;
    if (open(trigger.dataset.mrConfigure, trigger)) event.preventDefault();
  });

  // "View full details" leaves the configurator: hide it instantly (no exit animation) so the
  // product page is what the shopper sees, and so it isn't still open on Back (bfcache).
  const closeNow = () => {
    if (modal.hidden) return;
    modal.hidden = true;
    modal.dataset.state = 'closed';
    unlockScroll();
  };
  modal.addEventListener('click', (event) => {
    if (event.target.closest('.mr-builder__more')) closeNow();
  });
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) closeNow();
  });

  modal.querySelector('.configurator-backdrop').addEventListener('click', close);
  modal.querySelector('.configurator-close').addEventListener('click', close);
  modal.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') close();
    trapFocus(panel, event);
  });

  // Mobile: drag the handle down to dismiss
  const handle = modal.querySelector('.configurator-handle');
  let startY = null;
  handle?.addEventListener('touchstart', (e) => (startY = e.touches[0].clientY), { passive: true });
  handle?.addEventListener(
    'touchmove',
    (e) => {
      if (startY === null) return;
      const dy = Math.max(0, e.touches[0].clientY - startY);
      panel.style.transform = `translateY(${dy}px)`;
    },
    { passive: true }
  );
  handle?.addEventListener('touchend', (e) => {
    const dy = e.changedTouches[0].clientY - (startY ?? 0);
    panel.style.transform = '';
    startY = null;
    if (dy > 90) close();
  });
}

/* ------------------------------------------------------------------------
   Email popup (15s or 50% scroll, once; localStorage remembers dismiss/signup)
   ------------------------------------------------------------------------ */
function initPopup() {
  const popup = document.getElementById('mr-popup');
  if (!popup || popup.dataset.bound) return;
  popup.dataset.bound = 'true';
  const panel = popup.querySelector('.popup-panel');
  const pill = document.querySelector('[data-popup-open]');
  let opener = null;

  const show = (trigger) => {
    opener = trigger || document.activeElement;
    openLayer(popup);
    setTimeout(() => popup.querySelector('input[type=\'email\'], button')?.focus({ preventScroll: true }), 30);
  };
  const hide = () => {
    closeLayer(popup, 160);
    opener?.focus?.({ preventScroll: true });
  };

  // Only opens when the shopper taps the corner button (never on its own)
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-popup-open]');
    if (trigger) show(trigger);
  });
  // After a signup Shopify reloads the page: show the success message once
  if (popup.dataset.posted === 'true') {
    if (pill) pill.hidden = true;
    show(null);
  }

  popup.querySelectorAll('[data-popup-close]').forEach((el) => el.addEventListener('click', hide));
  popup.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') hide();
    trapFocus(panel, event);
  });
}

/* ------------------------------------------------------------------------
   Support chat panel (quick links)
   ------------------------------------------------------------------------ */
function initChat() {
  const chat = document.getElementById('mr-chat');
  if (!chat) return;
  const trigger = chat.querySelector('.chat-trigger');
  const panel = chat.querySelector('.chat-panel');
  const setOpen = (open) => {
    trigger.setAttribute('aria-expanded', String(open));
    if (open) {
      openLayer(panel);
      setTimeout(() => panel.querySelector('a, button')?.focus({ preventScroll: true }), 30);
    } else {
      closeLayer(panel, 160);
      trigger.focus({ preventScroll: true });
    }
  };
  trigger.addEventListener('click', () => setOpen(panel.hidden));
  panel.querySelector('[data-chat-close]').addEventListener('click', () => setOpen(false));
  panel.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') setOpen(false);
    trapFocus(panel, event);
  });
  // Lift the trigger above sticky add-to-cart bars on mobile
  const sticky = document.querySelector('.sticky-add-to-cart__bar');
  if (sticky) chat.classList.add('mr-chat--lifted');
}

/* ------------------------------------------------------------------------
   Shop All catalogue filters (client-side over real collection data)
   ------------------------------------------------------------------------ */
function initCatalogue() {
  const root = document.querySelector('[data-mr-catalogue]:not([data-bound])');
  if (!root) return;
  root.dataset.bound = 'true';
  const sections = [...root.querySelectorAll('[data-cat-section]')];
  const drawer = root.querySelector('.filter-drawer-wrap');
  const openBtn = root.querySelector('[data-filter-open]');

  const apply = () => {
    const cats = [...root.querySelectorAll('input[name="mr-cat"]:checked')].map((i) => i.value);
    const availability = root.querySelector('input[name="mr-stock"]:checked')?.value || 'all';
    const sort = root.querySelector('select[name="mr-sort"]')?.value || 'featured';

    sections.forEach((section) => {
      const show = !cats.length || cats.includes(section.dataset.catSection);
      section.hidden = !show;
      const grid = section.querySelector('.mr-grid');
      if (!grid) return;
      const cards = [...grid.querySelectorAll(':scope > .mr-card')];
      let visible = 0;
      cards.forEach((card) => {
        const ok =
          availability === 'all' ||
          (availability === 'in' && card.dataset.available === 'true') ||
          (availability === 'out' && card.dataset.available === 'false');
        card.hidden = !ok;
        if (ok) visible++;
      });
      const sorted = [...cards].sort((a, b) => {
        if (sort === 'price-asc') return a.dataset.price - b.dataset.price;
        if (sort === 'price-desc') return b.dataset.price - a.dataset.price;
        if (sort === 'newest') return b.dataset.created - a.dataset.created;
        return a.dataset.position - b.dataset.position;
      });
      sorted.forEach((card) => grid.appendChild(card));
      const empty = section.querySelector('.mr-cat__empty');
      if (empty) empty.hidden = visible > 0;
    });
  };

  root.addEventListener('change', (event) => {
    if (event.target.matches('input, select')) apply();
  });
  root.querySelector('[data-filter-clear]')?.addEventListener('click', () => {
    root.querySelectorAll('input[name="mr-cat"]').forEach((i) => (i.checked = false));
    const all = root.querySelector('input[name="mr-stock"][value="all"]');
    if (all) all.checked = true;
    apply();
  });

  if (drawer && openBtn) {
    const closeDrawer = () => {
      closeLayer(drawer, 200);
      unlockScroll();
      openBtn.focus({ preventScroll: true });
    };
    openBtn.addEventListener('click', () => {
      lockScroll();
      openLayer(drawer);
      setTimeout(() => drawer.querySelector('input, select, button')?.focus({ preventScroll: true }), 30);
    });
    drawer.querySelectorAll('[data-filter-close]').forEach((el) => el.addEventListener('click', closeDrawer));
    drawer.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closeDrawer();
      trapFocus(drawer.querySelector('.filter-drawer'), event);
    });
    // On desktop the same panel is inline; keep it visible there
    const mq = window.matchMedia('(min-width: 1024px)');
    const sync = () => {
      if (mq.matches) {
        drawer.hidden = false;
        drawer.dataset.state = 'inline';
      } else if (drawer.dataset.state === 'inline') {
        drawer.hidden = true;
        drawer.dataset.state = 'closed';
      }
    };
    mq.addEventListener('change', sync);
    sync();
  }
}

/* ------------------------------------------------------------------------
   Category jump: home tiles link to /collections/all#cat-<handle>.
   The first section (top of the page) needs no scroll; any other section is
   reached with a smooth scroll after the page settles (instant under reduced motion).
   ------------------------------------------------------------------------ */
function jumpToCategory(hash) {
  const root = document.querySelector('[data-mr-catalogue]');
  const match = String(hash || '').match(/cat-[\w-]+/);
  if (!root || !match) return;
  const anchor = document.getElementById(match[0]);
  if (!anchor) return;
  const section = anchor.closest('[data-cat-section]') || anchor;
  const first = root.querySelector('[data-cat-section]:not([hidden])');
  if (section === first) {
    window.scrollTo(0, 0);
    return;
  }
  section.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'start' });
  section.setAttribute('tabindex', '-1');
  section.focus({ preventScroll: true });
}

function initCategoryJump() {
  if (!/cat-[\w-]+/.test(location.hash)) {
    window.addEventListener('hashchange', () => setTimeout(() => jumpToCategory(location.hash), 60));
    return;
  }
  // Start at the top so the shopper sees where they landed, then glide down
  if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
  window.scrollTo(0, 0);
  setTimeout(() => jumpToCategory(location.hash), reducedMotion() ? 0 : 350);
  window.addEventListener('hashchange', () => setTimeout(() => jumpToCategory(location.hash), 60));
}

window.MR.jumpToCategory = jumpToCategory;

/* ------------------------------------------------------------------------
   Inline form validation (contact, track) — errors under each field
   ------------------------------------------------------------------------ */
function initValidation() {
  document.querySelectorAll('form.mr-form').forEach((form) => {
    form.setAttribute('novalidate', '');
    const check = (input) => {
      const error = form.querySelector(`#${input.id}-error`);
      let message = '';
      if (input.validity.valueMissing) message = `${input.dataset.label || 'This field'} is required.`;
      else if (input.validity.typeMismatch) message = 'Please enter a valid email address.';
      input.setAttribute('aria-invalid', message ? 'true' : 'false');
      if (error) {
        error.textContent = message;
        error.hidden = !message;
      }
      return !message;
    };
    form.querySelectorAll('input, textarea, select').forEach((input) => {
      if (!input.id) return;
      input.addEventListener('blur', () => input.value && check(input));
      input.addEventListener('input', () => input.getAttribute('aria-invalid') === 'true' && check(input));
    });
    form.addEventListener('submit', (event) => {
      const fields = [...form.querySelectorAll('input[required], textarea[required], select[required]')];
      const results = fields.map(check);
      if (results.includes(false)) {
        event.preventDefault();
        fields[results.indexOf(false)].focus();
      }
    });
  });
}

/* ------------------------------------------------------------------------
   Track Your Mini — hand off to the configured tracking page
   ------------------------------------------------------------------------ */
function initTracking() {
  const form = document.querySelector('[data-mr-track]');
  if (!form) return;
  form.addEventListener('submit', (event) => {
    if (event.defaultPrevented) return;
    event.preventDefault();
    const base = form.dataset.trackUrl;
    const order = form.querySelector('[name="order"]').value.trim().replace(/^#/, '');
    const email = form.querySelector('[name="email"]').value.trim();
    // Order details are only shown by the tracking provider after it verifies order number + email
    const url = new URL(base, window.location.origin);
    url.searchParams.set(form.dataset.orderParam || 'order', order);
    url.searchParams.set(form.dataset.emailParam || 'email', email);
    window.location.href = url.toString();
  });
}

/* ------------------------------------------------------------------------
   Header: slide-in mobile menu (swipe left / Escape / backdrop closes),
   search opens Horizon's search modal, cart toggles Horizon's cart drawer.
   ------------------------------------------------------------------------ */
function initHeader() {
  const menu = document.getElementById('MrMenu');
  const burger = document.querySelector('[data-mr-menu-open]');
  if (menu && burger && !menu.dataset.bound) {
    menu.dataset.bound = 'true';
    const openMenu = () => {
      burger.setAttribute('aria-expanded', 'true');
      lockScroll();
      openLayer(menu);
      setTimeout(() => menu.querySelector('a, button')?.focus({ preventScroll: true }), 30);
    };
    const closeMenu = () => {
      if (menu.hidden) return;
      burger.setAttribute('aria-expanded', 'false');
      closeLayer(menu, 200);
      unlockScroll();
      burger.focus({ preventScroll: true });
    };
    burger.addEventListener('click', openMenu);
    menu.querySelectorAll('[data-mr-menu-close]').forEach((el) => el.addEventListener('click', closeMenu));
    menu.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closeMenu();
      trapFocus(menu.querySelector('.mr-menu__panel'), event);
    });
    menu.querySelectorAll('a').forEach((a) => a.addEventListener('click', closeMenu));
    let startX = null;
    menu.addEventListener('touchstart', (e) => (startX = e.touches[0].clientX), { passive: true });
    menu.addEventListener('touchend', (e) => {
      if (startX !== null && startX - e.changedTouches[0].clientX > 60) closeMenu();
      startX = null;
    });
    window.MR.closeMenu = closeMenu;
  }

  // Search overlay (slides down from the header; 200ms in / 160ms out)
  const search = document.getElementById('MrSearch');
  if (search && !search.dataset.bound) {
    search.dataset.bound = 'true';
    let opener = null;
    const closeSearch = () => {
      if (search.hidden) return;
      closeLayer(search, 160);
      opener?.focus({ preventScroll: true });
    };
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-mr-search]');
      if (!trigger) return;
      event.preventDefault();
      window.MR.closeMenu?.();
      opener = trigger;
      openLayer(search);
      setTimeout(() => search.querySelector('input')?.focus({ preventScroll: true }), 30);
    });
    search.querySelectorAll('[data-search-close]').forEach((el) => el.addEventListener('click', closeSearch));
    search.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closeSearch();
      trapFocus(search.querySelector('.mr-search__panel'), event);
    });
  }
}

/* ------------------------------------------------------------------------
   Smooth scroll for in-page CTA links (instant under reduced motion)
   ------------------------------------------------------------------------ */
function initAnchors() {
  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[href^="#"]');
    if (!link || link.getAttribute('href').length < 2) return;
    let target = null;
    try {
      target = document.querySelector(link.getAttribute('href'));
    } catch {
      return; // not an element id (e.g. a route-style hash)
    }
    if (!target) return;
    event.preventDefault();
    target.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'start' });
    target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
  });
}

const init = () => {
  initHeader();
  initIntro();
  initHeroEntrance();
  initReveals();
  initCardAdd();
  initConfigurator();
  initPopup();
  initChat();
  initCatalogue();
  initCategoryJump();
  initValidation();
  initTracking();
  initAnchors();
};

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
else init();

// Theme editor: re-run reveals when sections reload
document.addEventListener('shopify:section:load', () => {
  initReveals();
  initConfigurator();
  initCatalogue();
});
